import React, { Component } from 'react'
import { toast } from 'react-toastify'
import { getString } from '../../../locale'
import Loader from '../../../components/loader'
import {
    getAutoRinseConfig,
    setAutoRinseConfig,
    getAutoRinseStatus,
    startAutoRinse,
    extendAutoRinse,
    cancelAutoRinse,
    AutoRinseConfig,
    AutoRinseStatus,
} from '../../../api/0x0005/general-settings'

type Props = {
    clientId: string
}

type State = {
    loading: boolean
    saving: boolean
    config: AutoRinseConfig
    status?: AutoRinseStatus
    pollInterval?: NodeJS.Timeout
}

const DEFAULT_CONFIG: AutoRinseConfig = {
    enabled: 0,
    hoursMask: 0,
    idleThresholdSec: 300,
    scheduleWaitSec: 0,
    durationSec: 60,
}

export default class AutoRinseTab extends Component<Props, State> {
    state: State = {
        loading: true,
        saving: false,
        config: DEFAULT_CONFIG,
    }

    componentDidMount() {
        this.loadData()
        this.startPolling()
    }

    componentWillUnmount() {
        if (this.state.pollInterval) clearInterval(this.state.pollInterval)
    }

    startPolling = () => {
        if (this.state.pollInterval) clearInterval(this.state.pollInterval)
        const pollInterval = setInterval(this.loadStatus, 1000)
        this.setState({ pollInterval })
    }

    loadStatus = async () => {
        try {
            const status = await getAutoRinseStatus(this.props.clientId)
            this.setState({ status })
        } catch (_) {}
    }

    loadData = async () => {
        this.setState({ loading: true })
        try {
            const [config, status] = await Promise.all([
                getAutoRinseConfig(this.props.clientId),
                getAutoRinseStatus(this.props.clientId),
            ])
            this.setState({ config, status, loading: false })
        } catch (error: any) {
            toast.error(error.message || getString('auto_rinse.load_error'))
            this.setState({ loading: false })
        }
    }

    setHour = (hour: number, enabled: boolean) => {
        const mask = 1 << hour
        const nextMask = enabled ? (this.state.config.hoursMask | mask) : (this.state.config.hoursMask & ~mask)
        this.setState({
            config: {
                ...this.state.config,
                hoursMask: nextMask >>> 0,
            },
        })
    }

    setNumber = (key: 'scheduleWaitSec' | 'durationSec', value: string) => {
        const parsed = Number.parseInt(value, 10)
        this.setState({
            config: {
                ...this.state.config,
                [key]: Number.isNaN(parsed) ? 0 : parsed,
            },
        })
    }

    setIdleThresholdMinutes = (value: string) => {
        const parsedMinutes = Number.parseInt(value, 10)
        this.setState({
            config: {
                ...this.state.config,
                idleThresholdSec: Number.isNaN(parsedMinutes) ? 0 : parsedMinutes * 60,
            },
        })
    }

    saveConfig = async () => {
        const { config } = this.state
        if (config.idleThresholdSec < 60 || config.idleThresholdSec > 3600) {
            toast.error(getString('auto_rinse.idle_range'))
            return
        }
        if (config.scheduleWaitSec < 0 || config.scheduleWaitSec > 86400) {
            toast.error(getString('auto_rinse.wait_range'))
            return
        }
        if (config.durationSec < 1 || config.durationSec > 3600) {
            toast.error(getString('auto_rinse.duration_range'))
            return
        }
        this.setState({ saving: true })
        try {
            await setAutoRinseConfig(this.props.clientId, config)
            toast.success(getString('auto_rinse.save_success'))
            await this.loadData()
        } catch (error: any) {
            toast.error(error.message || getString('auto_rinse.save_error'))
        } finally {
            this.setState({ saving: false })
        }
    }

    startNow = async () => {
        try {
            await startAutoRinse(this.props.clientId)
            toast.success(getString('auto_rinse.start_success'))
            await this.loadStatus()
        } catch (error: any) {
            toast.error(error.message || getString('auto_rinse.start_error'))
        }
    }

    extend = async () => {
        try {
            await extendAutoRinse(this.props.clientId, 30)
            toast.success(getString('auto_rinse.extend_success'))
            await this.loadStatus()
        } catch (error: any) {
            toast.error(error.message || getString('auto_rinse.extend_error'))
        }
    }

    waitForStopped = async (timeoutMs: number = 8000) => {
        const startedAt = Date.now()
        while (Date.now() - startedAt < timeoutMs) {
            const status = await getAutoRinseStatus(this.props.clientId)
            this.setState({ status })
            if (status.running === 0) return true
            await new Promise(resolve => setTimeout(resolve, 400))
        }
        return false
    }

    cancel = async () => {
        let cancelError = ''
        try {
            await cancelAutoRinse(this.props.clientId)
        } catch (error: any) {
            cancelError = error?.message || getString('auto_rinse.cancel_error')
        }

        try {
            const stopped = await this.waitForStopped()
            if (stopped) {
                toast.success(getString('auto_rinse.cancel_success'))
                return
            }
        } catch (_) {}

        if (cancelError) {
            toast.error(cancelError)
        } else {
            toast.error(getString('auto_rinse.cancel_error'))
        }
    }

    render() {
        const { loading, saving, config, status } = this.state
        if (loading) {
            return (
                <div className="flex items-center justify-center h-64">
                    <Loader />
                </div>
            )
        }

        return (
            <div className="p-6 space-y-6">
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white">{getString('auto_rinse.title')}</h1>

                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 space-y-4">
                    <label className="flex items-center gap-3">
                        <input
                            type="checkbox"
                            checked={config.enabled === 1}
                            onChange={e => this.setState({ config: { ...config, enabled: e.target.checked ? 1 : 0 } })}
                        />
                        <span className="text-gray-800 dark:text-white">{getString('auto_rinse.enabled')}</span>
                    </label>

                    <div>
                        <div className="text-sm text-gray-600 dark:text-gray-300 mb-2">{getString('auto_rinse.hours')}</div>
                        <div className="grid grid-cols-6 md:grid-cols-8 gap-2">
                            {Array.from({ length: 24 }).map((_, hour) => {
                                const checked = (config.hoursMask & (1 << hour)) !== 0
                                return (
                                    <label key={hour} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={e => this.setHour(hour, e.target.checked)}
                                        />
                                        {hour.toString().padStart(2, '0')}:00
                                    </label>
                                )
                            })}
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                        <label className="block">
                            <span className="text-sm text-gray-600 dark:text-gray-300">{getString('auto_rinse.idle_threshold_sec')}</span>
                            <input
                                type="number"
                                min={1}
                                max={60}
                                value={Math.max(1, Math.round(config.idleThresholdSec / 60))}
                                onChange={e => this.setIdleThresholdMinutes(e.target.value)}
                                className="mt-1 w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white"
                            />
                        </label>
                        <label className="block">
                            <span className="text-sm text-gray-600 dark:text-gray-300">{getString('auto_rinse.schedule_wait_sec')}</span>
                            <input
                                type="number"
                                min={0}
                                max={86400}
                                value={config.scheduleWaitSec}
                                onChange={e => this.setNumber('scheduleWaitSec', e.target.value)}
                                className="mt-1 w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white"
                            />
                        </label>
                        <label className="block">
                            <span className="text-sm text-gray-600 dark:text-gray-300">{getString('auto_rinse.duration_sec')}</span>
                            <input
                                type="number"
                                min={1}
                                max={3600}
                                value={config.durationSec}
                                onChange={e => this.setNumber('durationSec', e.target.value)}
                                className="mt-1 w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-800 dark:text-white"
                            />
                        </label>
                    </div>

                    <button
                        onClick={this.saveConfig}
                        disabled={saving}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
                    >
                        {saving ? getString('common.loading') : getString('common.save')}
                    </button>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 space-y-3">
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-white">{getString('auto_rinse.runtime')}</h2>
                    <div className="text-gray-700 dark:text-gray-300">
                        {getString('auto_rinse.running')}: {status?.running === 1 ? getString('auto_rinse.yes') : getString('auto_rinse.no')}
                    </div>
                    <div className="text-gray-700 dark:text-gray-300">
                        {getString('auto_rinse.remaining')}: {status?.remainingSeconds ?? 0}s
                    </div>
                    <div className="text-gray-700 dark:text-gray-300">
                        {getString('auto_rinse.last_rinse')}: {status?.hasLastRinse === 1 ? `${status.lastRinseElapsedSec}s` : '-'}
                    </div>
                    <div className="flex gap-3">
                        <button onClick={this.startNow} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded">
                            {getString('auto_rinse.start_now')}
                        </button>
                        <button
                            onClick={this.cancel}
                            disabled={status?.running !== 1}
                            className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded"
                        >
                            {getString('auto_rinse.cancel')}
                        </button>
                        <button
                            onClick={this.extend}
                            disabled={status?.canExtend !== 1}
                            className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400 text-white rounded"
                        >
                            {getString('auto_rinse.extend_30')}
                        </button>
                    </div>
                </div>
            </div>
        )
    }
}
